<template>
  <div id="page" class="container-fluid">
    <div class="row header mx-4 mt-3">
      <div class="col header-row">
        <h1>Episodic Payments</h1>
      </div>
      <div class="row filter-row mb-1">
          <div class="patient-search-section mr-2">
            <b-input-group class="patient-search-border text-right">
              <b-form-input ref="searchInput"
                            placeholder="Filter (Patient Name or ID)"
                            class="patient-search" v-model="searchTerm">
              </b-form-input>
              <b-input-group-append>
                <button class="btn btn-default patient-search-submit" type="button">
                  <font-awesome-icon icon="search"/>
                </button>
              </b-input-group-append>
            </b-input-group>
          </div>
            <b-form-group class="mr-2" label-class="ml-2" label="Start Date">
              <b-form-datepicker
                  class="orange-border form-control datepicker"
                  :hide-header="true"
                  :value-as-date="true"
                  :date-format-options="{month: 'short', day: 'numeric', year: 'numeric'}"
                  v-model="fromDate"/>
            </b-form-group>
            <b-form-group class="mr-2" label-class="ml-2" label="End Date">
            <b-form-datepicker
                offset="-100"
                text="Offset Dropdown"
                :value-as-date="true"
                :hide-header="true"
                v-model="toDate"
                :date-format-options="{month: 'short', day: 'numeric', year: 'numeric'}"
                class="orange-border form-control datepicker"/>
            </b-form-group>
          </div>
    </div>
    <div class="row header">
      <div class="col ml-4">
      </div>
      <div class="row text-right mx-4 pb-4">
        <b-btn @click.stop="statusPopoverShowing = !statusPopoverShowing"
               id="statusIds" variant="outline-orange" class="mr-2">
          <font-awesome-icon class="orange mr-2" :icon="[ 'far', 'bars-progress' ]"/>Statuses<b-badge v-show="selectedStatusIds.length > 0" variant="orange" class="ml-2">{{ selectedStatusIds.length }}</b-badge>
        </b-btn>
        <b-popover :show.sync="statusPopoverShowing"
                   target="statusIds" placement="bottomleft" triggers="">
          <b-form-group v-click-outside="hideStatusPopover" class="status-popover" label="Episode Status">
            <b-form-checkbox-group stacked v-model="selectedStatusIds" name="statusIds" :options="statusIds">
            </b-form-checkbox-group>
          </b-form-group>
        </b-popover>
        <b-btn @click.stop="employerPopoverShowing = !employerPopoverShowing"
               id="employerId" variant="outline-orange" class="mr-2">
          <font-awesome-icon class="orange mr-2" :icon="[ 'far', 'bars-progress' ]"/>Employer / TPA<b-badge v-show="selectedEmployerIds.length > 0" variant="orange" class="ml-2">{{ selectedEmployerIds.length }}</b-badge>
        </b-btn>
        <b-popover :show.sync="employerPopoverShowing"
                   target="employerId" placement="bottomleft" triggers="">
          <b-form-group v-click-outside="hideEmployerPopover" class="status-popover" label="Employer / TPA">
            <b-form-checkbox-group stacked v-model="selectedEmployerIds" name="employerIds" :options="employerOptions">
            </b-form-checkbox-group>
          </b-form-group>
        </b-popover>
        <button name="providers" class="btn btn-outline-orange providers-filter mr-2"
                @click.prevent="showingProviderList = !showingProviderList">
          <font-awesome-icon class="orange mr-2" :icon="[ 'fas', 'user-doctor' ]"/>Providers <b-badge v-show="selectedProviderIds.length > 0" variant="orange">{{ selectedProviderIds.length }}</b-badge>
        </button>
      </div>
    </div>

    <div class="container-fluid">
      <div class="row data-table-header">
        <div class="col-2"><a href="#" @click.prevent="sort('EpisodeName')">Episode Name {{ sortIndicator ('EpisodeName') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('PatientName')">Patient Name {{ sortIndicator ('PatientName') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('EpisodicCode')">Episodic Codes {{ sortIndicator ('EpisodicCode') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('EpisodeTerm')">Episodic Term {{ sortIndicator ('EpisodeTerm') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('EpisodeStartDate')">Start Date {{ sortIndicator ('EpisodeStartDate') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('Employer')">Employer / TPA {{ sortIndicator ('Employer') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('Provider')">Provider {{ sortIndicator ('Provider') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('Status')">Status {{ sortIndicator ('Status') }}</a></div>
        <div class="col-1"><a href="#" @click.prevent="sort('Balance')">Balance {{ sortIndicator ('Balance') }}</a></div>
        <div class="col-2"></div>
      </div>
      <div v-for="paymentDistribution in paymentDistributions" class="row data-table-row" :key="paymentDistribution.organizationBundlePaymentDistributionId">
        <div class="column col-2">{{ paymentDistribution.bundle.name }}</div>
        <div class="column col-1">{{ paymentDistribution.patient.lastName + ', ' + paymentDistribution.patient.firstName }}</div>
        <div class="column col-1"><b>{{ displayCode(paymentDistribution.bundle) }}</b>
          <div v-for="(code, index) in paymentDistribution.bundle.codes" v-if="paymentDistribution.bundle.codes" :key="'code-' + index">{{displayModifierCode(code)}}</div>
        </div>
        <div class="column col-1">{{ displayTerm(paymentDistribution) }}</div>
        <div class="column col-1"><div v-if="paymentDistribution.estimate.procedureDate">{{formatDate(paymentDistribution.estimate.procedureDate)}}</div></div>
        <div class="column col-1">{{ paymentDistribution.employer.name }}</div>
        <div class="column col-1">{{ providerName(paymentDistribution) }}</div>
        <div class="column col-1">{{ paymentDistribution.status }}</div>
        <div class="column col-1">${{ formatNumber(paymentDistribution.availableFunds) }}</div>
        <div class="column col-2 text-right">
          <button @click.prevent="showPayments(paymentDistribution)" class="btn btn-outline-orange m-1">
          <font-awesome-icon class="orange mr-2" :icon="['far', 'edit']"/>
            Review
          </button>
        </div>
      </div>
    </div>

    <div class="alert alert-secondary" role="alert" v-show="!paymentDistributions.length && !generalErrorMessage">
      &nbsp;&nbsp;No episodic payments were found for the specified date range, employers / TPAs, and statuses
    </div>

    <div class="row justify-content-center m-2">
      <div class="alert alert-danger col-6" role="alert" v-show="generalErrorMessage">
        {{ generalErrorMessage }}
      </div>
    </div>

    <loading-modal :visible="showingLoadingModal" :return-focus="$refs.searchInput"
                   @update:visible="visible => showingLoadingModal = visible"></loading-modal>
    <paginator :totalPages="totalPages" :currentPage="currentPage"
               @update:currentPage="newPage => currentPage = newPage"></paginator>
    <provider-select @saveProviderIds="providerSelectionChanged"
                     :selected-provider-ids="selectedProviderIds" :visible="showingProviderList"
                     @update:visible="visible => showingProviderList = visible"></provider-select>

  </div>
</template>

<script>
  import LoadingModal from '@/components/LoadingModal'
  import Paginator from '@/components/Paginator'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import moment from 'moment-timezone'
  import api from '@/services/api'
  import _ from 'lodash'
  import ProviderSelect from '@/components/ProviderSelect.vue'

  export default {
    name: 'BundlePaymentDistributions',

    components: { ProviderSelect, LoadingModal, Paginator },

    mixins: [ErrorsMixin],

    data () {
      return {
        paymentDistributions: [],
        showingLoadingModal: false,
        showingProviderList: false,
        selectedProviderIds: [],
        totalPages: 0,
        currentPage: 1,
        pageSize: 20,
        isLoadingBundles: false,
        isLoadingEmployers: false,
        sortBy: null,
        sortOrder: null,
        searchTerm: '',
        employerIds: [],
        selectedEmployerIds: [],
        employerOptions: [],
        fromDate: moment().startOf('day').subtract(90, 'day').utc().toJSON(),
        toDate: moment().endOf('day').utc().toJSON(),
        fromTime: '00:00',
        toTime: '23:59',
        day: moment().startOf('day').toJSON(),
        statusPopoverShowing: false,
        employerPopoverShowing: false,
        searchDebounceFn: null,
        restoringSettings: false,
        statusIds: [
          { text: 'Pending', value: 1 },
          { text: 'Approved', value: 2 },
          { text: 'Denied', value: 3 },
          { text: 'Closed', value: 4 }
        ],
        selectedStatusIds: [1, 2, 3],
        editingBundle: {
          organizationBundleId: 0
        }
      }
    },

    computed: {
      allEmployerIds () {
        return this.employerIds.map(aEmployer => {
          return aEmployer.organizationEmployerId
        })
      },
      fromDateTime () {
        if (this.fromTime) {
          return moment
            .utc(`${moment(this.fromDate).format('YYYY-MM-DD')} ${this.fromTime}`, 'YYYY-MM-DD HH:mm')
            .toISOString()
        } else {
          return moment.utc(this.fromDate).startOf('day').toISOString()
        }
      },
      toDateTime () {
        if (this.toTime) {
          return moment
            .utc(`${moment(this.toDate).format('YYYY-MM-DD')} ${this.toTime}`, 'YYYY-MM-DD HH:mm')
            .toISOString()
        } else {
          return moment.utc(this.toDate).endOf('day').toISOString()
        }
      }
    },

    watch: {
      currentPage (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.load()
        }
      },
      day (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      searchTerm (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      fromDate (newValue, oldValue) {
        if (!this.restoringSettings && newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      toDate (newValue, oldValue) {
        if (!this.restoringSettings && newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      fromTime (newValue, oldValue) {
        if (!this.restoringSettings && newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      toTime (newValue, oldValue) {
        if (!this.restoringSettings && newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      selectedStatusIds (newValue, oldValue) {
        if (!this.restoringSettings && newValue !== oldValue) {
          this.searchDebounceFn()
        }
      },
      selectedEmployerIds (newValue, oldValue) {
        if (!this.restoringSettings && newValue !== oldValue) {
          this.searchDebounceFn()
        }
      }
    },

    methods: {
      providerName (distribution) {
        if (distribution.estimate.providerName) {
          return distribution.estimate.providerName
        }
        return ''
      },

      displayTerm (paymentDistribution) {
        if (paymentDistribution.bundle.durationDays === 0) {
          return 'DOS'
        } else {
          return paymentDistribution.bundle.durationDays + 'D'
        }
      },

      load (silent = false) {
        if (!silent) {
          this.showingLoadingModal = true
        }
        if (this.isLoadingBundles) {
          return
        }
        this.isLoadingBundles = true
        const statusIdsToSend =
          this.selectedStatusIds.length === 0
            ? [1, 2, 3]
            : this.selectedStatusIds
        const employerIdsToSend =
          this.selectedEmployerIds.length === 0
            ? this.allEmployerIds
            : this.selectedEmployerIds
        const parameters = {
          searchTerm: this.searchTerm,
          statusIds: statusIdsToSend,
          fromDate: moment.utc(this.fromDateTime).toJSON(),
          toDate: moment.utc(this.toDateTime).toJSON(),
          employerIds: employerIdsToSend,
          providerIds: this.selectedProviderIds,
          pageSize: this.pageSize,
          page: this.currentPage,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder
        }
        api.post('/Dashboard/PaymentDistributions', parameters).then(response => {
          this.persistSettings()
          this.paymentDistributions = response.data.paymentDistributions
          this.currentPage = response.data.page
          this.selectedEmployer = response.data.selectedEmployer
          if (response.data.selectedEmployer) {
            this.selectedEmployerId = response.data.selectedEmployer.organizationEmployerId
          }
          this.totalPages = Math.ceil(response.data.totalPaymentDistributions / this.pageSize)
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$store.logErrorResponse(this.$options.name, 'LoadError', error)
            this.setFromApiErrorResponse(error)
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
            this.isLoadingBundles = false
          }, 100)
        })
      },

      loadEmployers () {
        this.isLoadingEmployers = true
        api.get('/Dashboard/Employers').then(response => {
          this.employerIds = response.data
          this.employerIds.forEach(employer => {
            this.employerOptions.push({
              text: employer.name,
              value: employer.organizationEmployerId
            })
          })
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$store.logErrorResponse(this.$options.name, 'LoadError', error)
            this.setFromApiErrorResponse(error)
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.isLoadingEmployers = false
          }, 100)
        })
      },
      providerSelectionChanged (selectedIds) {
        this.selectedProviderIds = selectedIds
        this.showingProviderList = false
        this.load()
      },
      persistSettings () {
        const bundlePaymentFilters = {
          fromTime: this.fromTime,
          toTime: this.toTime,
          statusIds: this.selectedStatusIds,
          fromDate: moment(this.fromDate).toISOString(),
          toDate: moment(this.toDate).toISOString(),
          employerIds: this.selectedEmployerIds,
          providerIds: this.selectedProviderIds,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder
        }
        localStorage.setItem('selectedBundlePaymentFilters', JSON.stringify(bundlePaymentFilters))
      },

      restoreSettings () {
        this.restoringSettings = true
        const settingBundlePaymentFilters = localStorage.getItem('selectedBundlePaymentFilters')
        if (settingBundlePaymentFilters) {
          const bundlePaymentFilters = JSON.parse(settingBundlePaymentFilters)
          if (bundlePaymentFilters.statusIds) {
            this.selectedStatusIds = bundlePaymentFilters.statusIds
          }
          if (bundlePaymentFilters.employerIds) {
            this.selectedEmployerIds = bundlePaymentFilters.employerIds
          }
          if (bundlePaymentFilters.providerIds) {
            this.selectedProviderIds = bundlePaymentFilters.providerIds
          }
          if (bundlePaymentFilters.fromDate && bundlePaymentFilters.fromDate !== 'null') {
            this.fromDate = bundlePaymentFilters.fromDate
          }
          if (bundlePaymentFilters.fromTime && bundlePaymentFilters.fromTime !== 'null') {
            this.fromTime = bundlePaymentFilters.fromTime
          }
          if (bundlePaymentFilters.toDate && bundlePaymentFilters.toDate !== 'null') {
            this.toDate = bundlePaymentFilters.toDate
          }
          if (bundlePaymentFilters.toTime && bundlePaymentFilters.toTime !== 'null') {
            this.toTime = bundlePaymentFilters.toTime
          }
          if (bundlePaymentFilters.sortBy && bundlePaymentFilters.sortBy !== 'null') {
            this.sortBy = bundlePaymentFilters.sortBy
          }
          if (bundlePaymentFilters.sortOrder && bundlePaymentFilters.sortOrder !== 'null') {
            this.sortOrder = bundlePaymentFilters.sortOrder
          }
        }
        this.$nextTick(() => {
          this.restoringSettings = false
        })
      },

      displayCode (bundle) {
        if (bundle.modifier) {
          return bundle.code + '-' + bundle.modifier
        }

        return bundle.code
      },

      displayModifierCode (code) {
        if (code) {
          return code.modifier
            ? `${code.code}-${code.modifier}`
            : code.code
        }
      },

      hideStatusPopover () {
        if (this.statusPopoverShowing) {
          this.$root.$emit('bv::hide::popover')
        }
      },

      hideEmployerPopover () {
        if (this.employerPopoverShowing) {
          this.$root.$emit('bv::hide::popover')
        }
      },

      sort (newOrder) {
        if (newOrder === this.sortBy) {
          if (this.sortOrder === 'Descending') {
            this.sortBy = null
            this.sortOrder = null
            this.load()

            return
          } else {
            this.sortOrder = 'Descending'
          }
        } else {
          this.sortOrder = 'Ascending'
        }

        this.sortBy = newOrder
        this.load()
      },

      sortIndicator (sortType) {
        if (sortType === this.sortBy) {
          if (this.sortOrder === 'Descending') {
            return '▼'
          } else {
            return '▲'
          }
        }

        return ''
      },

      formatDate (date) {
        if (date) {
          return moment(date).utc().format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      formatNumber (number) {
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return '0.00'
      },

      handleOrgChange (newOrg, event) {
        this.restoreSettings()
        this.loadEmployers()
        this.load()
      },

      showPayments (bundleDistribution) {
        this.$router.push({ name: 'bundle-payment', params: { id: bundleDistribution.organizationBundlePaymentDistributionId } })
      }
    },

    created () {
      this.$store.$on('organization:changed', this.handleOrgChange)
      this.searchDebounceFn = _.debounce(() => {
        if (this.searchTerm === '') {
          this.totalPages = Math.ceil(this.paymentDistributions.totalPaymentDistributions / this.pageSize)
          this.currentPage = 1
        } else {
          this.currentPage = 1
          this.totalPages = 0
        }
        this.load()
      }, 500)
      this.restoreSettings()
      this.loadEmployers()
      this.load()
    }
  }
</script>

<style scoped>
.patient-search-border {
  /* background: white; */
  border: 1px solid rgb(255, 70, 0);
  border-radius: 5px;
}

.patient-search {
  border: none;
  border-radius: 4px 0 0 4px;
  box-shadow: none;
  padding-right: 0px;
  min-width: 230px;
  height: 36px;
}

.patient-search:focus {
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075),
  inset 0 0 1px rgba(255, 70, 0, .6);
}

.patient-search-submit {
  border: none;
  border-radius: 0 4px 4px 0;
}

.patient-search-submit:focus,
.patient-search-submit:focus:active,
.patient-search-submit:active {
  outline: 5px auto rgba(255, 70, 0, 0.1);
}

.patient-search-submit:hover {
  color: rgb(255, 70, 0);
}

.header-row {
  margin-top: 28px;
}

.patient-search-section {
  margin-top: 28px;
}
</style>
